import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Modal, Button, Form, Row, Col, Pagination, Table } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";
// import file from "../../files/CostEstimationSetup.xlsx";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import CatalogFilterChip from "../../components/sa/CatalogFilterChip";
import TableSortSelect from "../../components/TableSortSelect";
import { TABLE_SORT } from "../../utils/tableSort";
import {
  getUseCases,
  addOrUpdateUseCase,
  deleteUseCase,
  bulkUploadUseCases,
  getIndustries,
  getSubSectors,
  getBusinessFunctions,
  getDataAIValueLevers,
  getBusinessValueDrivers,
  getFunctionalValueDrivers
} from "../../services/CostEstimationsetupApi";
import { Check, Download, Lightbulb, Pencil, Search, Trash2, Upload } from "lucide-react";
import "../../assets/css/dashboard.css";

const UseCases = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);

  // Use Cases data from API
  const [useCases, setUseCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);

  // Filter states
  const [filters, setFilters] = useState({
    industryId: '',
    sectorId: '',
    functionId: '',
    subFunctionId: '',
    dataAILeverId: '',
    dataAISubLeverId: '',
    businessValueDriverId: '',
    businessSubValueDriverId: '',
    functionalValueDriverId: '',
    functionalSubValueDriverId: '',
    searchTerm: '',
    isActive: true
  });
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);

  // API Functions
  const fetchUseCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        ...filters,
        page: currentPage,
        pageSize: pageSize,
        sort: sortKey,
      };

      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await getUseCases(params);
      
      if (response && response.data) {
        setUseCases(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setUseCases([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching use cases:', err);
      setError(err.message || 'Failed to fetch use cases');
      setUseCases([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize, sortKey]);

  // Fetch Industries API
  const fetchIndustries = useCallback(async () => {
    try {
      setIndustriesLoading(true);
      const response = await getIndustries({
        isActive: true,
        page: 1,
        pageSize: 1000 // Get all industries
      });
      
      if (response && response.data) {
        setIndustries(response.data.data || []);
      } else {
        setIndustries([]);
      }
    } catch (err) {
      console.error('Error fetching industries:', err);
      setIndustries([]);
    } finally {
      setIndustriesLoading(false);
    }
  }, []);

  // Fetch Sectors API
  const fetchSectors = useCallback(async (industryId = null) => {
    try {
      setSectorsLoading(true);
      
      // If no industryId provided, get all sectors
      const params = {
        isActive: true,
        page: 1,
        pageSize: 1000 // Get all sectors
      };
      
      // Add industryId if provided
      if (industryId) {
        params.industryId = industryId;
      }
      
      const response = await getSubSectors(params);
      
      if (response && response.data) {
        setSectors(response.data.data || []);
      } else {
        setSectors([]);
      }
    } catch (err) {
      console.error('Error fetching sectors:', err);
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  // Fetch Business Functions API
  const fetchBusinessFunctions = useCallback(async () => {
    try {
      setBusinessFunctionsLoading(true);
      const response = await getBusinessFunctions({
        isActive: true,
        page: 1,
        pageSize: 1000 // Get all business functions
      });
      
      if (response && response.data) {
        const functions = response.data.data || [];
        setBusinessFunctions(functions);
        
        // Extract all sub-functions from business functions
        const allSubFunctions = [];
        functions.forEach(func => {
          if (func.subBusinessFunctions && Array.isArray(func.subBusinessFunctions)) {
            func.subBusinessFunctions.forEach(subFunc => {
              allSubFunctions.push({
                ...subFunc,
                functionId: func.id,
                functionName: func.name
              });
            });
          }
        });
        setSubBusinessFunctions(allSubFunctions);
      } else {
        setBusinessFunctions([]);
        setSubBusinessFunctions([]);
      }
    } catch (err) {
      console.error('Error fetching business functions:', err);
      setBusinessFunctions([]);
      setSubBusinessFunctions([]);
    } finally {
      setBusinessFunctionsLoading(false);
    }
  }, []);

  // Fetch Data/AI Value Levers API
  const fetchDataAIValueLevers = useCallback(async () => {
    try {
      setDataAILeversLoading(true);
      const response = await getDataAIValueLevers({
        isActive: true,
        page: 1,
        pageSize: 1000 // Get all data/AI levers
      });
      
      if (response && response.data) {
        const levers = response.data.data || [];
        setDataAILevers(levers);
      } else {
        setDataAILevers([]);
      }
    } catch (err) {
      console.error('Error fetching data/AI levers:', err);
      setDataAILevers([]);
    } finally {
      setDataAILeversLoading(false);
    }
  }, []);

  // Fetch Business Value Drivers API
  const fetchBusinessValueDrivers = useCallback(async () => {
    try {
      setBusinessValueDriversLoading(true);
      const response = await getBusinessValueDrivers({
        isActive: true,
        page: 1,
        pageSize: 1000 // Get all business value drivers
      });
      
      if (response && response.data) {
        const drivers = response.data.data || [];
        setBusinessValueDrivers(drivers);
      } else {
        setBusinessValueDrivers([]);
      }
    } catch (err) {
      console.error('Error fetching business value drivers:', err);
      setBusinessValueDrivers([]);
    } finally {
      setBusinessValueDriversLoading(false);
    }
  }, []);

  // Fetch Functional Value Drivers API
  const fetchFunctionalValueDrivers = useCallback(async () => {
    try {
      setFunctionalValueDriversLoading(true);
      const response = await getFunctionalValueDrivers({
        isActive: true,
        page: 1,
        pageSize: 1000 // Get all functional value drivers
      });
      
      if (response && response.data) {
        const drivers = response.data.data || [];
        setFunctionalValueDrivers(drivers);
      } else {
        setFunctionalValueDrivers([]);
      }
    } catch (err) {
      console.error('Error fetching functional value drivers:', err);
      setFunctionalValueDrivers([]);
    } finally {
      setFunctionalValueDriversLoading(false);
    }
  }, []);

  // Load use cases on component mount and when filters change
  useEffect(() => {
    const timerId = window.setTimeout(() => {
      fetchUseCases();
    }, 300);

    return () => window.clearTimeout(timerId);
  }, [fetchUseCases]);

  // Load industries on component mount
  useEffect(() => {
    fetchIndustries();
  }, [fetchIndustries]);

  // Load sectors on component mount
  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  // Load business functions on component mount
  useEffect(() => {
    fetchBusinessFunctions();
  }, [fetchBusinessFunctions]);

  // Load data/AI levers on component mount
  useEffect(() => {
    fetchDataAIValueLevers();
  }, [fetchDataAIValueLevers]);

  // Load business value drivers on component mount
  useEffect(() => {
    fetchBusinessValueDrivers();
  }, [fetchBusinessValueDrivers]);

  // Load functional value drivers on component mount
  useEffect(() => {
    fetchFunctionalValueDrivers();
  }, [fetchFunctionalValueDrivers]);

  // Use Case form data
  const [useCaseForm, setUseCaseForm] = useState({
    id: 0,
    caseId: "",
    industryId: "",
    industryName: "",
    sectorId: "",
    sectorName: "",
    functionId: "",
    functionName: "",
    subFunctionId: "",
    subFunctionName: "",
    dataAILeverId: "",
    dataAILeverName: "",
    dataAISubLeverId: "",
    dataAISubLeverName: "",
    businessValueDriverId: "",
    businessValueDriverName: "",
    businessSubValueDriverId: "",
    businessValueSubDriverName: "",
    functionalValueDriverId: "",
    functionalValueDriverName: "",
    functionalSubValueDriverId: "",
    functionalSubValueDriverName: "",
    businessCapability: "",
    dataAI: "",
    title: "",
    description: "",
    dataCapability: "",
    dataTechnicalCapability: "",
    aiCapability: "",
    aiTechnicalCapability: "",
    improvementActions: "",
    indicator: "",
    isActive: true
  });

  // Dropdown data should be fetched from APIs
  const [industries, setIndustries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [businessFunctions, setBusinessFunctions] = useState([]);
  const [subBusinessFunctions, setSubBusinessFunctions] = useState([]);
  const [dataAILevers, setDataAILevers] = useState([]);
  const [businessValueDrivers, setBusinessValueDrivers] = useState([]);
  const [functionalValueDrivers, setFunctionalValueDrivers] = useState([]);
  
  // Loading states for dropdowns
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [businessFunctionsLoading, setBusinessFunctionsLoading] = useState(false);
  const [dataAILeversLoading, setDataAILeversLoading] = useState(false);
  const [businessValueDriversLoading, setBusinessValueDriversLoading] = useState(false);
  const [functionalValueDriversLoading, setFunctionalValueDriversLoading] = useState(false);

  // Use Cases data is now managed by API

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 100,
    totalPages: 1,
    totalCount: 0,
  });

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const handleShowUseCase = useCallback((useCase = null) => {
    if (useCase) {
      setUseCaseForm({
        id: useCase.id,
        caseId: useCase.caseId || "",
        industryId: useCase.industryId || "",
        industryName: useCase.industry?.name || "",
        sectorId: useCase.sectorId || "",
        sectorName: useCase.sector?.name || "",
        functionId: useCase.functionId || "",
        functionName: useCase.function?.name || "",
        subFunctionId: useCase.subFunctionId || "",
        subFunctionName: useCase.subFunction?.name || "",
        dataAILeverId: useCase.dataAILeverId || "",
        dataAILeverName: useCase.dataAILever?.name || "",
        dataAISubLeverId: useCase.dataAISubLeverId || "",
        dataAISubLeverName: useCase.dataAISubLever?.name || "",
        businessValueDriverId: useCase.businessValueDriverId || "",
        businessValueDriverName: useCase.businessValueDriver?.name || "",
        businessSubValueDriverId: useCase.businessSubValueDriverId || "",
        businessValueSubDriverName: useCase.businessSubValueDriver?.name || "",
        functionalValueDriverId: useCase.functionalValueDriverId || "",
        functionalValueDriverName: useCase.functionalValueDriver?.name || "",
        functionalSubValueDriverId: useCase.functionalSubValueDriverId || "",
        functionalSubValueDriverName: useCase.functionalSubValueDriver?.name || "",
        businessCapability: useCase.businessCapability || "",
        dataAI: useCase.dataAI || "",
        title: useCase.title || "",
        description: useCase.description || "",
        dataCapability: useCase.dataCapability || "",
        dataTechnicalCapability: useCase.dataTechnicalCapability || "",
        aiCapability: useCase.aiCapability || "",
        aiTechnicalCapability: useCase.aiTechnicalCapability || "",
        improvementActions: useCase.improvementActions || "",
        indicator: useCase.indicator || "",
        isActive: useCase.isActive !== undefined ? useCase.isActive : true
      });
    } else {
      setUseCaseForm({
        id: 0,
        caseId: "",
        industryId: "",
        industryName: "",
        sectorId: "",
        sectorName: "",
        functionId: "",
        functionName: "",
        subFunctionId: "",
        subFunctionName: "",
        dataAILeverId: "",
        dataAILeverName: "",
        dataAISubLeverId: "",
        dataAISubLeverName: "",
        businessValueDriverId: "",
        businessValueDriverName: "",
        businessSubValueDriverId: "",
        businessValueSubDriverName: "",
        functionalValueDriverId: "",
        functionalValueDriverName: "",
        functionalSubValueDriverId: "",
        functionalSubValueDriverName: "",
        businessCapability: "",
        dataAI: "",
        title: "",
        description: "",
        dataCapability: "",
        dataTechnicalCapability: "",
        aiCapability: "",
        aiTechnicalCapability: "",
        improvementActions: "",
        indicator: "",
        isActive: true
      });
    }
    
    // Fetch sectors for the selected industry when editing
    if (useCase && useCase.industryId) {
      fetchSectors(parseInt(useCase.industryId));
    }
    
    // Business functions are already loaded on component mount
    // No need to fetch them again as they don't depend on other selections
    
    setShowModal(true);
  }, [fetchSectors]);

  const handleClose = useCallback(() => {
    setShowModal(false);
          setUseCaseForm({
        id: 0,
        caseId: "",
        industryId: "",
        industryName: "",
        sectorId: "",
        sectorName: "",
        functionId: "",
        functionName: "",
        subFunctionId: "",
        subFunctionName: "",
        dataAILeverId: "",
        dataAILeverName: "",
        dataAISubLeverId: "",
        dataAISubLeverName: "",
        businessValueDriverId: "",
        businessValueDriverName: "",
        businessSubValueDriverId: "",
        businessValueSubDriverName: "",
        functionalValueDriverId: "",
        functionalValueDriverName: "",
        functionalSubValueDriverId: "",
        functionalSubValueDriverName: "",
        businessCapability: "",
        dataAI: "",
        title: "",
        description: "",
        dataCapability: "",
        dataTechnicalCapability: "",
        aiCapability: "",
        aiTechnicalCapability: "",
        improvementActions: "",
        indicator: "",
        isActive: true
      });
  }, []);

  const handleUseCaseChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'industryId') {
      // When industry changes, clear dependent fields and find industry name
      const selectedIndustry = industries.find(ind => ind.id === parseInt(value));
      setUseCaseForm(prev => ({
        ...prev,
        industryId: value,
        industryName: selectedIndustry ? selectedIndustry.name : '',
        sectorId: '', // Clear sector when industry changes
        sectorName: '',
        functionId: '', // Clear function when industry changes
        functionName: '',
        subFunctionId: '', // Clear sub function when industry changes
        subFunctionName: ''
      }));
      
      // Fetch sectors for the selected industry
      if (value) {
        fetchSectors(parseInt(value));
      } else {
        fetchSectors(); // Fetch all sectors if no industry selected
      }
    } else if (name === 'sectorId') {
      // When sector changes, find sector name
      const selectedSector = sectors.find(sec => sec.id === parseInt(value));
      setUseCaseForm(prev => ({
        ...prev,
        sectorId: value,
        sectorName: selectedSector ? selectedSector.name : ''
      }));
    } else if (name === 'functionId') {
      // When business function changes, find function name and clear sub-function
      const selectedFunction = businessFunctions.find(func => func.id === parseInt(value));
      setUseCaseForm(prev => ({
        ...prev,
        functionId: value,
        functionName: selectedFunction ? selectedFunction.name : '',
        subFunctionId: '', // Clear sub-function when function changes
        subFunctionName: ''
      }));
    } else if (name === 'subFunctionId') {
      // When sub-function changes, find sub-function name
      const selectedSubFunction = subBusinessFunctions.find(subFunc => subFunc.id === parseInt(value));
      setUseCaseForm(prev => ({
        ...prev,
        subFunctionId: value,
        subFunctionName: selectedSubFunction ? selectedSubFunction.name : ''
      }));
    } else if (name === 'dataAILeverId') {
      // When data/AI lever changes, find lever name and clear sub-lever
      const selectedLever = dataAILevers.find(lever => lever.id === parseInt(value));
      setUseCaseForm(prev => ({
        ...prev,
        dataAILeverId: value,
        dataAILeverName: selectedLever ? selectedLever.name : '',
        dataAISubLeverId: '', // Clear sub-lever when lever changes
        dataAISubLeverName: ''
      }));
    } else if (name === 'dataAISubLeverId') {
      // When data/AI sub-lever changes, find sub-lever name
      const selectedSubLever = dataAILevers.find(lever => 
        lever.dataAIValueSubLevers && lever.dataAIValueSubLevers.find(subLever => subLever.id === parseInt(value))
      );
      const subLever = selectedSubLever ? selectedSubLever.dataAIValueSubLevers.find(subLever => subLever.id === parseInt(value)) : null;
      setUseCaseForm(prev => ({
        ...prev,
        dataAISubLeverId: value,
        dataAISubLeverName: subLever ? subLever.name : ''
      }));
    } else if (name === 'businessValueDriverId') {
      // When business value driver changes, find driver name and clear sub-driver
      const selectedDriver = businessValueDrivers.find(driver => driver.id === parseInt(value));
      setUseCaseForm(prev => ({
        ...prev,
        businessValueDriverId: value,
        businessValueDriverName: selectedDriver ? selectedDriver.name : '',
        businessSubValueDriverId: '', // Clear sub-driver when driver changes
        businessValueSubDriverName: ''
      }));
    } else if (name === 'businessSubValueDriverId') {
      // When business sub-value driver changes, find sub-driver name
      const selectedSubDriver = businessValueDrivers.find(driver => 
        driver.businessSubValueDrivers && driver.businessSubValueDrivers.find(subDriver => subDriver.id === parseInt(value))
      );
      const subDriver = selectedSubDriver ? selectedSubDriver.businessSubValueDrivers.find(subDriver => subDriver.id === parseInt(value)) : null;
      setUseCaseForm(prev => ({
        ...prev,
        businessSubValueDriverId: value,
        businessValueSubDriverName: subDriver ? subDriver.name : ''
      }));
    } else if (name === 'functionalValueDriverId') {
      // When functional value driver changes, find driver name and clear sub-driver
      const selectedDriver = functionalValueDrivers.find(driver => driver.id === parseInt(value));
      setUseCaseForm(prev => ({
        ...prev,
        functionalValueDriverId: value,
        functionalValueDriverName: selectedDriver ? selectedDriver.name : '',
        functionalSubValueDriverId: '', // Clear sub-driver when driver changes
        functionalSubValueDriverName: ''
      }));
    } else if (name === 'functionalSubValueDriverId') {
      // When functional sub-value driver changes, find sub-driver name
      const selectedSubDriver = functionalValueDrivers.find(driver => 
        driver.functionalSubValueDrivers && driver.functionalSubValueDrivers.find(subDriver => subDriver.id === parseInt(value))
      );
      const subDriver = selectedSubDriver ? selectedSubDriver.functionalSubValueDrivers.find(subDriver => subDriver.id === parseInt(value)) : null;
      setUseCaseForm(prev => ({
        ...prev,
        functionalSubValueDriverId: value,
        functionalSubValueDriverName: subDriver ? subDriver.name : ''
      }));
    } else {
      setUseCaseForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  }, [industries, sectors, businessFunctions, subBusinessFunctions, dataAILevers, businessValueDrivers, functionalValueDrivers, fetchSectors]);

  const handleUseCaseSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const {
        caseId,
        title,
        industryId,
        sectorId,
        functionId,
        subFunctionId,
        dataAILeverId,
        dataAISubLeverId,
        businessValueDriverId,
        businessSubValueDriverId,
        functionalValueDriverId,
        functionalSubValueDriverId,
        businessCapability,
        dataAI,
        description,
        dataCapability,
        dataTechnicalCapability,
        aiCapability,
        aiTechnicalCapability,
        improvementActions,
        indicator,
        isActive
      } = useCaseForm;

      if (caseId && title && industryId && sectorId && functionId && subFunctionId) {
        try {
          setLoading(true);
          
          // Prepare data for API call
          const useCaseData = {
            id: useCaseForm.id || 0,
            caseId: caseId.trim(),
            industryId: parseInt(industryId),
            sectorId: parseInt(sectorId),
            functionId: parseInt(functionId),
            subFunctionId: parseInt(subFunctionId),
            dataAILeverId: dataAILeverId ? parseInt(dataAILeverId) : 0,
            dataAISubLeverId: dataAISubLeverId ? parseInt(dataAISubLeverId) : 0,
            businessValueDriverId: businessValueDriverId ? parseInt(businessValueDriverId) : 0,
            businessSubValueDriverId: businessSubValueDriverId ? parseInt(businessSubValueDriverId) : 0,
            functionalValueDriverId: functionalValueDriverId ? parseInt(functionalValueDriverId) : 0,
            functionalSubValueDriverId: functionalSubValueDriverId ? parseInt(functionalSubValueDriverId) : 0,
            businessCapability: businessCapability || '',
            dataAI: dataAI || '',
            title: title.trim(),
            description: description || '',
            dataCapability: dataCapability || '',
            dataTechnicalCapability: dataTechnicalCapability || '',
            aiCapability: aiCapability || '',
            aiTechnicalCapability: aiTechnicalCapability || '',
            improvementActions: improvementActions || '',
            indicator: indicator || '',
            isActive: isActive !== undefined ? isActive : true
          };

          // Call the API
          const response = await addOrUpdateUseCase(useCaseData);
          
          if (response) {
            // Refresh the use cases list
            await fetchUseCases();
            
            if (useCaseForm.id === 0) {
              dashboardToast.success("Use Case created successfully");
            } else {
              dashboardToast.success("Use Case updated successfully");
            }
            handleClose();
          }
        } catch (err) {
          console.error('Error saving use case:', err);
          dashboardToast.error(err.message || "Failed to save Use Case");
        } finally {
          setLoading(false);
        }
      } else {
        dashboardToast.error("Please fill in all required fields");
      }
    },
    [useCaseForm, handleClose, fetchUseCases]
  );

  const handleDelete = useCallback((item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  }, []);

  const openViewModal = useCallback((useCase) => {
    setViewRecord(useCase);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    
    try {
      setLoading(true);
      
      // Call the delete API
      await deleteUseCase(itemToDelete.id);
      
      // Refresh the use cases list after successful deletion
      await fetchUseCases();
      
      dashboardToast.success("Use Case deleted successfully");
    } catch (err) {
      console.error('Error deleting use case:', err);
      dashboardToast.error(err.message || "Failed to delete Use Case");
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, fetchUseCases]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  }, []);

  // File upload handler
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (example: only allow Excel files)
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
      ];
      
      if (allowedTypes.includes(file.type)) {
        setSelectedFile(file);
        // No popup message on file selection
      } else {
        // Use setTimeout to prevent toast conflicts
        setTimeout(() => {
          dashboardToast.error('Please select a valid Excel or CSV file');
        }, 100);
        event.target.value = ''; // Clear the input
      }
    }
  }, []);

  // Submit file handler
  const handleFileSubmit = useCallback(async () => {
    if (!selectedFile) {
      // Use setTimeout to prevent toast conflicts
      setTimeout(() => {
        dashboardToast.error('Please select a file first');
      }, 100);
      return;
    }

    try {
      // Clear any existing toasts before starting upload
      dashboardToast.dismiss();
      setFileUploadLoading(true);
      
      // Call the bulk upload API
      const response = await bulkUploadUseCases(selectedFile);
      
      if (response) {
        // Clear any filters to show all data including newly uploaded
        const clearedFilters = {
          industryId: '',
          sectorId: '',
          functionId: '',
          subFunctionId: '',
          dataAILeverId: '',
          dataAISubLeverId: '',
          businessValueDriverId: '',
          businessSubValueDriverId: '',
          functionalValueDriverId: '',
          functionalSubValueDriverId: '',
          searchTerm: '',
          isActive: true
        };
        
        setFilters(clearedFilters);
        setCurrentPage(1);
        
        // Fetch use cases with cleared filters and first page
        try {
          setLoading(true);
          setError(null);
          
          const params = {
            ...clearedFilters,
            page: 1,
            pageSize: pageSize
          };

          // Remove empty filter values
          Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
              delete params[key];
            }
          });

          const fetchResponse = await getUseCases(params);
          
          if (fetchResponse && fetchResponse.data) {
            setUseCases(fetchResponse.data.data || []);
            setTotalCount(fetchResponse.data.totalCount || 0);
          } else {
            setUseCases([]);
            setTotalCount(0);
          }
        } catch (fetchErr) {
          console.error('Error fetching use cases after upload:', fetchErr);
          setError(fetchErr.message || 'Failed to fetch use cases');
        } finally {
          setLoading(false);
        }
        
        // Show success message after a short delay to ensure state is updated
        setTimeout(() => {
          dashboardToast.success('File uploaded successfully! Use cases have been imported.');
        }, 500);
        
        setSelectedFile(null);
        
        // Reset file input
        const fileInput = document.getElementById('file-upload-input');
        if (fileInput) {
          fileInput.value = '';
        }
      }
      
    } catch (err) {
      console.error('Error uploading file:', err);
      // Use setTimeout to prevent toast conflicts
      setTimeout(() => {
        dashboardToast.error(err.message || 'Failed to upload file');
      }, 100);
    } finally {
      setFileUploadLoading(false);
    }
  }, [selectedFile, pageSize]);

  // Download file handler
  const handleDownloadTemplate = useCallback(() => {
    try {
      // Create a link element to trigger download
      const link = document.createElement('a');
      // Try multiple possible paths for the file
      const possiblePaths = [
        '/CostEstimationSetup.xlsx',
        '/files/CostEstimationSetup.xlsx',
        './CostEstimationSetup.xlsx',
        './files/CostEstimationSetup.xlsx'
      ];
      
      // Set the first path and let the browser handle fallback
      link.href = possiblePaths[0];
      link.download = 'CostEstimationSetup.xlsx';
      link.target = '_blank';
      
      // Add error handling for the link
      link.onerror = () => {
        // Try alternative paths
        for (let i = 1; i < possiblePaths.length; i++) {
          const altLink = document.createElement('a');
          altLink.href = possiblePaths[i];
          altLink.download = 'CostEstimationSetup.xlsx';
          altLink.target = '_blank';
          document.body.appendChild(altLink);
          altLink.click();
          document.body.removeChild(altLink);
          break;
        }
      };
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Use setTimeout to prevent toast conflicts
      setTimeout(() => {
        dashboardToast.success('Template file download started');
      }, 100);
    } catch (err) {
      console.error('Error downloading file:', err);
      // Use setTimeout to prevent toast conflicts
      setTimeout(() => {
        dashboardToast.error('Failed to download template file. Please check if the file exists in the public folder.');
      }, 100);
    }
  }, []);

  const goToPage = useCallback(n => {
    const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
    if (pg !== apiPageInfo.pageNumber) {
      setApiPageInfo(prev => ({ ...prev, pageNumber: pg }));
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.totalPages]);

  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalCount / pageSize)));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));



  // Calculate pagination info
  useEffect(() => {
    const totalCount = useCases.length;
    const totalPages = Math.ceil(totalCount / apiPageInfo.pageSize);
    setApiPageInfo(prev => ({
      ...prev,
      totalPages,
      totalCount
    }));
  }, [useCases, apiPageInfo.pageSize]);

  // Get paginated Use Cases (API already handles pagination)
  const getPaginatedUseCases = () => {
    return useCases; // API already returns paginated data
  };

  // Helper function to get names from API response objects
  const getIndustryName = (useCase) => useCase?.industry?.name || 'N/A';
  const getSectorName = (useCase) => useCase?.sector?.name || 'N/A';
  const getFunctionName = (useCase) => useCase?.function?.name || 'N/A';
  const getSubFunctionName = (useCase) => useCase?.subFunction?.name || 'N/A';
  const getDataAILeverName = (useCase) => useCase?.dataAILever?.name || 'N/A';
  const getDataAISubLeverName = (useCase) => useCase?.dataAISubLever?.name || 'N/A';
  const getBusinessValueDriverName = (useCase) => useCase?.businessValueDriver?.name || 'N/A';
  const getBusinessSubValueDriverName = (useCase) => useCase?.businessSubValueDriver?.name || 'N/A';
  const getFunctionalValueDriverName = (useCase) => useCase?.functionalValueDriver?.name || 'N/A';
  const getFunctionalSubValueDriverName = (useCase) => useCase?.functionalSubValueDriver?.name || 'N/A';

  return (
    <DashboardShell isMenuCollapsed={isMenuCollapsed}
      sidebar={<Menu onSelect={handleMenuSelection} selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />}>
      <SaListPageShell topBarTitle="Use Cases Management">
            <div className="dash-frame-parent1">
              <div className="dash-list-toolbar">
                <div className="dash-list-toolbar__intro">
                  <h5 className="dash-list-toolbar__title">Manage Use Cases</h5>
                  <p className="dash-list-toolbar__desc">
                    Create use cases individually or upload them from a spreadsheet.
                  </p>
                </div>
                <div className="dash-list-toolbar__actions">
                  <div className="dash-search-field dash-table-search">
                    <Search className="dash-search-field__icon" aria-hidden />
                    <Form.Control
                      type="text"
                      placeholder="Search by title…"
                      value={filters.searchTerm}
                      onChange={(e) => {
                        setCurrentPage(1);
                        setFilters(prev => ({ ...prev, searchTerm: e.target.value }));
                      }}
                      className="dash-search-field__input"
                    />
                  </div>
                  <TableSortSelect variant="chip"
                    value={sortKey}
                    onChange={(nextSort) => {
                      setSortKey(nextSort);
                      setCurrentPage(1);
                    }}
                    disabled={loading}
                  />
                  <Button onClick={() => handleShowUseCase()} className="btn btn-dashboard-primary sa-create-btn">
                    Create Use Case
                  </Button>

                  <div style={{ position: "relative" }}>
                    <input
                      type="file"
                      id="file-upload-input"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileUpload}
                      style={{ display: "none" }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => document.getElementById('file-upload-input').click()}
                      disabled={fileUploadLoading}
                      style={{
                        borderColor: "#A72B2A",
                        color: "#A72B2A",
                      }}
                      onMouseEnter={(e) => {
                        if (!fileUploadLoading) {
                          e.target.style.backgroundColor = "#A72B2A";
                          e.target.style.color = "white";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!fileUploadLoading) {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = "#A72B2A";
                        }
                      }}
                    >
                      <Upload className="me-2" />
                      {fileUploadLoading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </div>

                  {selectedFile && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleFileSubmit}
                      disabled={fileUploadLoading}
                      className="btn btn-dashboard-primary"
                    >
                      <Check className="me-2" />
                      Submit
                    </Button>
                  )}

                  {selectedFile && (
                    <div style={{
                      fontSize: "0.9rem",
                      color: "#A72B2A",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      maxWidth: "12rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }} title={selectedFile.name}>
                      <Check />
                      {selectedFile.name}
                    </div>
                  )}

                  <div className="download-template-container">
                    <Button
                      variant="outline-secondary"
                      onClick={handleDownloadTemplate}
                      style={{
                        borderColor: "#A72B2A",
                        color: "#A72B2A",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#A72B2A";
                        e.target.style.color = "white";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.color = "#A72B2A";
                      }}
                    >
                      <Download className="me-2" />
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-2">
                  <CatalogFilterChip
                    label="Industry"
                    value={String(filters.industryId ?? "")}
                    onChange={(value) => setFilters(prev => ({ ...prev, industryId: value }))}
                    disabled={industriesLoading}
                    options={industries.map(industry => ({ value: String(industry.id), label: industry.name }))}
                  />
                </div>
                <div className="col-md-2">
                  <CatalogFilterChip
                    label="Sector"
                    value={String(filters.sectorId ?? "")}
                    onChange={(value) => setFilters(prev => ({ ...prev, sectorId: value }))}
                    disabled={sectorsLoading}
                    options={sectors.map(sector => ({ value: String(sector.id), label: sector.name }))}
                  />
                </div>
                <div className="col-md-2">
                  <CatalogFilterChip
                    label="Status"
                    value={String(filters.isActive)}
                    onChange={(value) => setFilters(prev => ({ ...prev, isActive: value === 'true' }))}
                    showAll={false}
                    options={[
                      { value: "true", label: "Active" },
                      { value: "false", label: "Inactive" },
                    ]}
                  />
                </div>
                <div className="col-md-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setFilters({
                      industryId: '',
                      sectorId: '',
                      functionId: '',
                      subFunctionId: '',
                      dataAILeverId: '',
                      dataAISubLeverId: '',
                      businessValueDriverId: '',
                      businessSubValueDriverId: '',
                      functionalValueDriverId: '',
                      functionalSubValueDriverId: '',
                      searchTerm: '',
                      isActive: true
                    })}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
              {loading ? (
                <ContentLoader label="Loading…" />
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : (
                <Fragment>
                  <div className="app-table-wrap">
                    <Table className="table table-striped">
                      <thead>
                        <tr>
                          <th>S.NO</th>
                          <th>Case ID</th>
                          <th>Title</th>
                          <th>Industry</th>
                          <th>Sector</th>
                          <th>Function</th>
                          <th>Data/AI Lever</th>
                          <th>Business Value Driver</th>
                          <th>Functional Value Driver</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedUseCases().length === 0 ? (
                          <tr>
                            <td colSpan="11" className="text-center">
                              No Use Cases found.
                            </td>
                          </tr>
                        ) : (
                          getPaginatedUseCases().map((useCase, index) => (
                            <tr
                              key={useCase.id}
                              className="rdm-row-clickable"
                              onClick={() => openViewModal(useCase)}
                            >
                              <td>
                                {index + 1}
                              </td>
                              <td>
                                {useCase.caseId}
                              </td>
                              <td>{useCase.title}</td>
                              <td>{getIndustryName(useCase)}</td>
                              <td>{getSectorName(useCase)}</td>
                              <td>{getFunctionName(useCase)}</td>
                              <td>{getDataAILeverName(useCase)}</td>
                              <td>{getBusinessValueDriverName(useCase)}</td>
                              <td>{getFunctionalValueDriverName(useCase)}</td>
                              <td>
                                <span className={`badge ${useCase.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                  {useCase.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                  <Button
                                    variant="outline-secondary" className="btn-list-edit"
                                    size="sm"
                                    onClick={() => handleShowUseCase(useCase)}
                                  >
                                    <Pencil />
                                  </Button>
                                  <Button
                                    className="btn btn-list-delete"
                                    size="sm"
                                    onClick={() => handleDelete(useCase)}
                                    disabled={loading}
                                  >
                                    <Trash2 />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {Math.ceil(totalCount / pageSize) > 1 && (
                    <Pagination className="justify-content-center">
                      <Pagination.Prev
                        onClick={prevPage}
                        disabled={currentPage === 1}
                      />
                      {Array.from(
                        { length: Math.ceil(totalCount / pageSize) },
                        (_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => setCurrentPage(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        )
                      )}
                      <Pagination.Next
                        onClick={nextPage}
                        disabled={currentPage === Math.ceil(totalCount / pageSize)}
                      />
                    </Pagination>
                  )}
                </Fragment>
              )}
            </div>
      </SaListPageShell>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="xl"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          {useCaseForm.id > 0 ? (
            <>
              <Lightbulb className="h-5 w-5 me-2" />
              <Modal.Title>Edit Use Case</Modal.Title>
            </>
          ) : (
            <>
              <Lightbulb className="h-5 w-5 me-2" />
              <Modal.Title>Create Use Case</Modal.Title>
            </>
          )}
        </Modal.Header>

        <Form onSubmit={handleUseCaseSubmit}>
          <Modal.Body className="px-4">
            <Row>
              <Col md={6}>
                <Form.Group controlId="formCaseId" className="mb-3">
                  <Form.Label>Case ID <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="caseId"
                    value={useCaseForm.caseId}
                    onChange={handleUseCaseChange}
                    required
                    placeholder="Enter case ID"
                    maxLength="50"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formTitle" className="mb-3">
                  <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={useCaseForm.title}
                    onChange={handleUseCaseChange}
                    required
                    placeholder="Enter use case title"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formIndustry" className="mb-3">
                  <Form.Label>Industry <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="industryId"
                    value={useCaseForm.industryId}
                    onChange={handleUseCaseChange}
                    required
                    disabled={industriesLoading}
                  >
                    <option value="">
                      {industriesLoading ? 'Loading industries...' : 'Select Industry'}
                    </option>
                    {industries.map(industry => (
                      <option key={industry.id} value={industry.id}>
                        {industry.name}
                      </option>
                    ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.industryId && !industries.find(ind => ind.id === parseInt(useCaseForm.industryId)) && (
                      <option value={useCaseForm.industryId} disabled>
                        {useCaseForm.industryName || `Industry ${useCaseForm.industryId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formSector" className="mb-3">
                  <Form.Label>Sector <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="sectorId"
                    value={useCaseForm.sectorId}
                    onChange={handleUseCaseChange}
                    required
                    disabled={!useCaseForm.industryId || sectorsLoading}
                  >
                    <option value="">
                      {sectorsLoading ? 'Loading sectors...' : 'Select Sector'}
                    </option>
                    {sectors.map(sector => (
                      <option key={sector.id} value={sector.id}>
                        {sector.name}
                      </option>
                    ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.sectorId && !sectors.find(sec => sec.id === parseInt(useCaseForm.sectorId)) && (
                      <option value={useCaseForm.sectorId} disabled>
                        {useCaseForm.sectorName || `Sector ${useCaseForm.sectorId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formFunction" className="mb-3">
                  <Form.Label>Business Function <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="functionId"
                    value={useCaseForm.functionId}
                    onChange={handleUseCaseChange}
                    required
                    disabled={businessFunctionsLoading}
                  >
                    <option value="">
                      {businessFunctionsLoading ? 'Loading functions...' : 'Select Business Function'}
                    </option>
                    {businessFunctions.map(func => (
                      <option key={func.id} value={func.id}>
                        {func.name}
                      </option>
                    ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.functionId && !businessFunctions.find(func => func.id === parseInt(useCaseForm.functionId)) && (
                      <option value={useCaseForm.functionId} disabled>
                        {useCaseForm.functionName || `Function ${useCaseForm.functionId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formSubFunction" className="mb-3">
                  <Form.Label>Sub Function <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="subFunctionId"
                    value={useCaseForm.subFunctionId}
                    onChange={handleUseCaseChange}
                    required
                    disabled={!useCaseForm.functionId || businessFunctionsLoading}
                  >
                    <option value="">
                      {businessFunctionsLoading ? 'Loading sub-functions...' : 'Select Sub Function'}
                    </option>
                    {subBusinessFunctions
                      .filter(subFunc => subFunc.functionId === parseInt(useCaseForm.functionId))
                      .map(subFunc => (
                        <option key={subFunc.id} value={subFunc.id}>
                          {subFunc.name}
                        </option>
                      ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.subFunctionId && !subBusinessFunctions.find(subFunc => subFunc.id === parseInt(useCaseForm.subFunctionId)) && (
                      <option value={useCaseForm.subFunctionId} disabled>
                        {useCaseForm.subFunctionName || `Sub Function ${useCaseForm.subFunctionId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formDataAILever" className="mb-3">
                  <Form.Label>Data/AI Lever</Form.Label>
                  <Form.Select
                    name="dataAILeverId"
                    value={useCaseForm.dataAILeverId}
                    onChange={handleUseCaseChange}
                    disabled={dataAILeversLoading}
                  >
                    <option value="">
                      {dataAILeversLoading ? 'Loading levers...' : 'Select Data/AI Lever'}
                    </option>
                    {dataAILevers.map(lever => (
                      <option key={lever.id} value={lever.id}>
                        {lever.name}
                      </option>
                    ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.dataAILeverId && !dataAILevers.find(lever => lever.id === parseInt(useCaseForm.dataAILeverId)) && (
                      <option value={useCaseForm.dataAILeverId} disabled>
                        {useCaseForm.dataAILeverName || `Data/AI Lever ${useCaseForm.dataAILeverId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formDataAISubLever" className="mb-3">
                  <Form.Label>Data/AI Sub Lever</Form.Label>
                  <Form.Select
                    name="dataAISubLeverId"
                    value={useCaseForm.dataAISubLeverId}
                    onChange={handleUseCaseChange}
                    disabled={!useCaseForm.dataAILeverId || dataAILeversLoading}
                  >
                    <option value="">
                      {dataAILeversLoading ? 'Loading sub-levers...' : 'Select Data/AI Sub Lever'}
                    </option>
                    {dataAILevers
                      .find(lever => lever.id === parseInt(useCaseForm.dataAILeverId))
                      ?.dataAIValueSubLevers?.map(subLever => (
                        <option key={subLever.id} value={subLever.id}>
                          {subLever.name}
                        </option>
                      ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.dataAISubLeverId && !dataAILevers.find(lever => 
                      lever.dataAIValueSubLevers?.find(subLever => subLever.id === parseInt(useCaseForm.dataAISubLeverId))
                    ) && (
                      <option value={useCaseForm.dataAISubLeverId} disabled>
                        {useCaseForm.dataAISubLeverName || `Data/AI Sub Lever ${useCaseForm.dataAISubLeverId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formBusinessValueDriver" className="mb-3">
                  <Form.Label>Business Value Driver</Form.Label>
                  <Form.Select
                    name="businessValueDriverId"
                    value={useCaseForm.businessValueDriverId}
                    onChange={handleUseCaseChange}
                    disabled={businessValueDriversLoading}
                  >
                    <option value="">
                      {businessValueDriversLoading ? 'Loading drivers...' : 'Select Business Value Driver'}
                    </option>
                    {businessValueDrivers.map(driver => (
                      <option key={driver.id} value={driver.id}>
                        {driver.name}
                      </option>
                    ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.businessValueDriverId && !businessValueDrivers.find(driver => driver.id === parseInt(useCaseForm.businessValueDriverId)) && (
                      <option value={useCaseForm.businessValueDriverId} disabled>
                        {useCaseForm.businessValueDriverName || `Business Value Driver ${useCaseForm.businessValueDriverId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formBusinessSubValueDriver" className="mb-3">
                  <Form.Label>Business Sub Value Driver</Form.Label>
                  <Form.Select
                    name="businessSubValueDriverId"
                    value={useCaseForm.businessSubValueDriverId}
                    onChange={handleUseCaseChange}
                    disabled={!useCaseForm.businessValueDriverId || businessValueDriversLoading}
                  >
                    <option value="">
                      {businessValueDriversLoading ? 'Loading sub-drivers...' : 'Select Business Sub Value Driver'}
                    </option>
                    {businessValueDrivers
                      .find(driver => driver.id === parseInt(useCaseForm.businessValueDriverId))
                      ?.businessSubValueDrivers?.map(subDriver => (
                        <option key={subDriver.id} value={subDriver.id}>
                          {subDriver.name}
                        </option>
                      ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.businessSubValueDriverId && !businessValueDrivers.find(driver => 
                      driver.businessSubValueDrivers?.find(subDriver => subDriver.id === parseInt(useCaseForm.businessSubValueDriverId))
                    ) && (
                      <option value={useCaseForm.businessSubValueDriverId} disabled>
                        {useCaseForm.businessValueSubDriverName || `Business Sub Value Driver ${useCaseForm.businessSubValueDriverId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formFunctionalValueDriver" className="mb-3">
                  <Form.Label>Functional Value Driver</Form.Label>
                  <Form.Select
                    name="functionalValueDriverId"
                    value={useCaseForm.functionalValueDriverId}
                    onChange={handleUseCaseChange}
                    disabled={functionalValueDriversLoading}
                  >
                    <option value="">
                      {functionalValueDriversLoading ? 'Loading drivers...' : 'Select Functional Value Driver'}
                    </option>
                    {functionalValueDrivers.map(driver => (
                      <option key={driver.id} value={driver.id}>
                        {driver.name}
                      </option>
                    ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.functionalValueDriverId && !functionalValueDrivers.find(driver => driver.id === parseInt(useCaseForm.functionalValueDriverId)) && (
                      <option value={useCaseForm.functionalValueDriverId} disabled>
                        {useCaseForm.functionalValueDriverName || `Functional Value Driver ${useCaseForm.functionalValueDriverId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formFunctionalSubValueDriver" className="mb-3">
                  <Form.Label>Functional Sub Value Driver</Form.Label>
                  <Form.Select
                    name="functionalSubValueDriverId"
                    value={useCaseForm.functionalSubValueDriverId}
                    onChange={handleUseCaseChange}
                    disabled={!useCaseForm.functionalValueDriverId || functionalValueDriversLoading}
                  >
                    <option value="">
                      {functionalValueDriversLoading ? 'Loading sub-drivers...' : 'Select Functional Sub Value Driver'}
                    </option>
                    {functionalValueDrivers
                      .find(driver => driver.id === parseInt(useCaseForm.functionalValueDriverId))
                      ?.functionalSubValueDrivers?.map(subDriver => (
                        <option key={subDriver.id} value={subDriver.id}>
                          {subDriver.name}
                        </option>
                      ))}
                    {/* Show current value if not in dropdown list */}
                    {useCaseForm.functionalSubValueDriverId && !functionalValueDrivers.find(driver => 
                      driver.functionalSubValueDrivers?.find(subDriver => subDriver.id === parseInt(useCaseForm.functionalSubValueDriverId))
                    ) && (
                      <option value={useCaseForm.functionalSubValueDriverId} disabled>
                        {useCaseForm.functionalSubValueDriverName || `Functional Sub Value Driver ${useCaseForm.functionalSubValueDriverId}`}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formBusinessCapability" className="mb-3">
                  <Form.Label>Business Capability</Form.Label>
                  <Form.Control
                    type="text"
                    name="businessCapability"
                    value={useCaseForm.businessCapability}
                    onChange={handleUseCaseChange}
                    placeholder="Enter business capability"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formDataAI" className="mb-3">
                  <Form.Label>Data/AI</Form.Label>
                  <Form.Control
                    type="text"
                    name="dataAI"
                    value={useCaseForm.dataAI}
                    onChange={handleUseCaseChange}
                    placeholder="Enter data/AI description"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group controlId="formDescription" className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={useCaseForm.description}
                    onChange={handleUseCaseChange}
                    placeholder="Enter use case description"
                    maxLength="1000"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formDataCapability" className="mb-3">
                  <Form.Label>Data Capability</Form.Label>
                  <Form.Control
                    type="text"
                    name="dataCapability"
                    value={useCaseForm.dataCapability}
                    onChange={handleUseCaseChange}
                    placeholder="Enter data capability"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formDataTechnicalCapability" className="mb-3">
                  <Form.Label>Data Technical Capability</Form.Label>
                  <Form.Control
                    type="text"
                    name="dataTechnicalCapability"
                    value={useCaseForm.dataTechnicalCapability}
                    onChange={handleUseCaseChange}
                    placeholder="Enter data technical capability"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formAICapability" className="mb-3">
                  <Form.Label>AI Capability</Form.Label>
                  <Form.Control
                    type="text"
                    name="aiCapability"
                    value={useCaseForm.aiCapability}
                    onChange={handleUseCaseChange}
                    placeholder="Enter AI capability"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formAITechnicalCapability" className="mb-3">
                  <Form.Label>AI Technical Capability</Form.Label>
                  <Form.Control
                    type="text"
                    name="aiTechnicalCapability"
                    value={useCaseForm.aiTechnicalCapability}
                    onChange={handleUseCaseChange}
                    placeholder="Enter AI technical capability"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formImprovementActions" className="mb-3">
                  <Form.Label>Improvement Actions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="improvementActions"
                    value={useCaseForm.improvementActions}
                    onChange={handleUseCaseChange}
                    placeholder="Enter improvement actions"
                    maxLength="1000"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formIndicator" className="mb-3">
                  <Form.Label>Indicator</Form.Label>
                  <Form.Control
                    type="text"
                    name="indicator"
                    value={useCaseForm.indicator}
                    onChange={handleUseCaseChange}
                    placeholder="Enter success indicator"
                    maxLength="200"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formIsActive" className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="checkbox"
                      name="isActive"
                      checked={useCaseForm.isActive}
                      onChange={handleUseCaseChange}
                      label="Active"
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn btn-dashboard-primary"
            >
              {useCaseForm.id ? 'Update Use Case' : 'Create Use Case'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{itemToDelete?.title}</strong> (Case ID: {itemToDelete?.caseId})?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewRecord?.title || "Use Case Details"}
        subtitle={viewRecord?.caseId ? `Case ID: ${viewRecord.caseId}` : "Use case record"}
        size="xl"
        fields={[
          { label: "Case ID", value: viewRecord?.caseId },
          { label: "Title", value: viewRecord?.title },
          { label: "Description", value: viewRecord?.description, fullWidth: true },
          { label: "Industry", value: viewRecord ? getIndustryName(viewRecord) : undefined },
          { label: "Sector", value: viewRecord ? getSectorName(viewRecord) : undefined },
          { label: "Function", value: viewRecord ? getFunctionName(viewRecord) : undefined },
          { label: "Sub Function", value: viewRecord ? getSubFunctionName(viewRecord) : undefined },
          { label: "Data/AI Lever", value: viewRecord ? getDataAILeverName(viewRecord) : undefined },
          { label: "Data/AI Sub Lever", value: viewRecord ? getDataAISubLeverName(viewRecord) : undefined },
          { label: "Business Value Driver", value: viewRecord ? getBusinessValueDriverName(viewRecord) : undefined },
          { label: "Business Sub Value Driver", value: viewRecord ? getBusinessSubValueDriverName(viewRecord) : undefined },
          { label: "Functional Value Driver", value: viewRecord ? getFunctionalValueDriverName(viewRecord) : undefined },
          { label: "Functional Sub Value Driver", value: viewRecord ? getFunctionalSubValueDriverName(viewRecord) : undefined },
          { label: "Business Capability", value: viewRecord?.businessCapability, fullWidth: true },
          { label: "Data/AI", value: viewRecord?.dataAI, fullWidth: true },
          { label: "Data Capability", value: viewRecord?.dataCapability, fullWidth: true },
          { label: "Data Technical Capability", value: viewRecord?.dataTechnicalCapability, fullWidth: true },
          { label: "AI Capability", value: viewRecord?.aiCapability, fullWidth: true },
          { label: "AI Technical Capability", value: viewRecord?.aiTechnicalCapability, fullWidth: true },
          { label: "Improvement Actions", value: viewRecord?.improvementActions, fullWidth: true },
          { label: "Indicator", value: viewRecord?.indicator },
          { label: "Active", value: Boolean(viewRecord?.isActive) },
        ]}
        actions={[
          {
            type: "edit",
            onClick: () => {
              const useCase = viewRecord;
              closeViewModal();
              if (useCase) handleShowUseCase(useCase);
            },
          },
          {
            type: "delete",
            onClick: () => {
              const useCase = viewRecord;
              closeViewModal();
              if (useCase) handleDelete(useCase);
            },
          },
        ]}
      />

    </DashboardShell>
  );
};

export default UseCases;
